import {
    Box,
    Button,
    capitalize,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {Abladestelle} from "../../api";
import DataTable from "../data/DataTable";
import AbladestellenArchivSelect from "./AbladestellenArchivSelect";
import useCustomFilters from "../data/useCustomFilters";

export type AbladestelleFilter = {
    search?: string;
    filtered?: string;
    archivierte?: string;
};

export type Props = Omit<DataRequest<AbladestelleFilter>, "filter"> &
    Partial<Pick<DataRequest<AbladestelleFilter>, "filter">>;

const tableName: string = "abladestellen"

export default function AbladestellenDatatable({...input}: Props) {
    const request = useDataRequest<AbladestelleFilter>({filter: {}, ...input});
    const [filteredState, setFilteredState] = useState("");
    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <AbladestellenResults
                request={request}
                setFilteredState={setFilteredState}
            />
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<AbladestelleFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<AbladestelleFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
            archivierte: "nur aktive",
        }
    });

    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {t: translation} = useT();

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            archivierte: currentCustomFilter.archivierte,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <AbladestellenArchivSelect
                        value={currentCustomFilter.archivierte ?? "nur aktive"}
                        onChange={(val) => setCustomField("archivierte", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/neu-abladestelle");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <AbladestellenArchivSelect
                        value={currentCustomFilter.archivierte ?? "nur aktive"}
                        onChange={(val) => setCustomField("archivierte", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{position: "absolute", top: 0, right: 0}}
                        onClick={() => {
                            navigate("/neu-abladestelle");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </>
            )}
        </Box>
    );
}

function AbladestellenResults({
                                  request,
                                  setFilteredState,
                              }: {
    request: DataRequestState<AbladestelleFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<AbladestelleFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const loadAbladestellen: DataLoader<AbladestelleFilter, Abladestelle> =
        useCallback(
            async (params) =>
                (await getApi()).abladestellen.sucheAbladestellen({...params}),
            [getApi]
        );
    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadAbladestellen}
            onRowClick={(row) => {
                navigate("/abladestellen/" + row.id);
            }}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<Abladestelle>> {
    const {t: translation} = useT("abladestelle");

    return useMemo(
        () => [
            {
                field: "kurzname",
                headerName: capitalize(translation("abladestelle-attribute-short-name")),
                flex: 1,
            },
            {
                field: "firma",
                headerName: capitalize(translation("abladestelle-attribute-company")),
                flex: 2,
            },
            {
                field: "zusatz",
                headerName: capitalize(translation("abladestelle-attribute-addition")),
                flex: 2,
            },
            {
                field: "ort",
                headerName: capitalize(translation("abladestelle-attribute-location")),
                flex: 2,
            },
            {
                field: "abladestelleTyp",
                headerName: capitalize(translation("abladestelle-attribute-type")),
                flex: 2,
            },
        ],
        [translation]
    );
}
